import type { CorporationType, Country, GovernanceRole } from '@prisma/client';
import type {
  MembershipConfig,
  PartnershipConfig,
  SharesConfig,
} from './compute';

export type OwnershipVariant = 'shares' | 'membership' | 'partnership';

export interface OwnershipFieldVisibility {
  visible: boolean;
  editable: boolean;
}
export type OwnershipFieldSettings = Record<string, OwnershipFieldVisibility>;

export type PartnershipStructure = 'gp_and_lp' | 'lp_only';

// Map the entity type's holding type (its legal form's, as content gives it,
// surfaced on the supported-corporations entry) to the node variant. Returns
// undefined for an unknown/unconfigured holding type.
export function holdingTypeToVariant(
  holdingType: string | null | undefined
): OwnershipVariant | undefined {
  switch (holdingType) {
    case 'SHARES':
      return 'shares';
    case 'MEMBERSHIP':
      return 'membership';
    case 'PARTNERSHIP':
      return 'partnership';
    default:
      return undefined;
  }
}

// An owner staged in the modal but not yet persisted. The cap table is written
// as one reconcile at form submit (T5), so the modal only collects identity —
// %, units, and capital are set in the table afterward.
export interface StagedIndividualOwner {
  kind: 'individual';
  // Existing director when linked; undefined when the person is created inline.
  directorId?: number;
  firstName: string;
  lastName?: string;
  dateOfBirth?: string; // YYYY-MM-DD
  // Absent for an owner seeded from a person already on the company: the write
  // links them by directorId and never reads this, and the summary renders blank
  // rather than inventing a country.
  nationality?: Country;
}

export interface StagedEntityOwner {
  kind: 'entity';
  // Existing workspace corporation when linked; undefined when created inline.
  corporationId?: number;
  companyLegalName: string;
  incorporationCountry: Country;
  jurisdiction?: string;
  entityType?: CorporationType;
  // The legal form the content offers. entityType is the legacy vocabulary and names only a third
  // of them, so without this the server has to re-derive a form from the type and guesses wherever
  // several share one.
  entityLegalFormId?: string;
}

export type StagedOwner = StagedIndividualOwner | StagedEntityOwner;

export interface OwnershipEntry {
  owner: StagedOwner;
  // for owners added in the form. Used to delete removed owners on edit (reconcile).
  stakeholdingId?: string;
  // Partnership only — exactly one entry may be the General Partner.
  isGeneralPartner?: boolean;
  // Set in the ownership table (T4), not the modal.
  ownershipPercent?: number;
  // Membership only — per-member governance role, set in the table when the
  // class is manager-managed; otherwise every member is a Member.
  governanceRole?: GovernanceRole;
  // Answers from the row's linked sub-form (entity owners only) — registration
  // number, registered address, incorporation documents, representative.
  details?: Record<string, unknown>;
}

// Node value in `formData[fieldId]`, mirroring KeyPersons' `{ selectedIds, entries }`.
export interface OwnershipFieldValue {
  selectedIds: string[];
  entries: Record<string, OwnershipEntry>;
  // Resolved currency (ISO code) from the entity's country, persisted so summaries
  // render money fields (capital, price/share, price/unit) with it.
  currency?: string;
  country?: string;
  // Partnership only — persisted so summaries (which only receive the value)
  // know whether a General Partner role exists at all.
  partnershipStructure?: PartnershipStructure;
  // Shares-variant structural config (Share Capital / Authorized / ESOP / Issued).
  shares?: SharesConfig;
  // Membership-variant structural config (Total capital / Issue units / Total units).
  membership?: MembershipConfig;
  // Partnership-variant structural config (total capital).
  partnership?: PartnershipConfig;
}

// Per-variant copy for the modal + table + add button.
export const VARIANT_COPY: Record<
  OwnershipVariant,
  { noun: string; modalTitle: string; primaryButton: string; addButton: string }
> = {
  shares: {
    noun: 'shareholder',
    modalTitle: 'Add shareholder',
    primaryButton: 'Create shareholder',
    addButton: 'Add shareholder',
  },
  membership: {
    noun: 'member',
    modalTitle: 'Add member',
    primaryButton: 'Create member',
    addButton: 'Add member',
  },
  partnership: {
    noun: 'partner',
    modalTitle: 'Add partner',
    primaryButton: 'Add partner',
    addButton: 'Add partner',
  },
};

export function ownerDisplayName(owner: StagedOwner): string {
  if (owner.kind === 'entity') return owner.companyLegalName;
  return [owner.firstName, owner.lastName].filter(Boolean).join(' ');
}
